import React, { useCallback, useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../services/translate';
import { Pagination } from '../../components/Pagination';
import moment from 'moment';
import { NavigationProp, RouteProp, useFocusEffect, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { crmService } from '../../services/central-api/crm';
import MyDataTable from '../../components/MyDataTable';
import { CRMParamList } from '../../typings/Params';
import { GenericPaginatedParameters } from '../../services/central-api/base-service';
import CustomButton from '../../components/CustomButton';
import DropdownButton from '../../components/DropdownButton';
import Container from '../../components/Container';
import Header from '../../components/Header';
import Card from '../../components/Card';
import DataTableHeader from '../../components/DataTableHeader';
import FilterInput from '../../components/Filter/components/FilterInput';
import DataTableContainer from '../../components/DataTableContainer';

export default function AlarmCentersList() {

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const navigation = useNavigation<NavigationProp<CRMParamList>>();

    const [alarmCenters, setAlarmCenters] = useState<AlarmCenter[]>([]);

    const route = useRoute<RouteProp<CRMParamList, 'AlarmCentersList'>>();

    // filters
    const [filters, setFilters] = useState<GenericPaginatedParameters>({
        textFilter: route.params.textFilter ?? '',
        page: Math.max(route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });

    const [textFilter, setTextFilter] = useState(filters.textFilter);
    const [totalItems, setTotalItems] = useState(0);

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            getAlarmCentersList();
        }
    }, [isFocused, navigation, filters]));

    async function getAlarmCentersList() {
        try {
            setIsLoading(true);
            const response = await crmService.getAlarmCenterPaginated(filters);
            setAlarmCenters(response.rows);
            setTotalItems(response.count);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    function handleFilters() {
        setFilters({
            ...filters,
            textFilter: textFilter !== '' ? textFilter : undefined,
            page: 0
        });

        navigation.setParams({
            textFilter: textFilter !== '' ? textFilter : undefined,
            page: 1
        });
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            handleFilters();
        }
    }

    return (
        <Container>
            <Header
                title={translate('alarmCenters')}
                actions={<CustomButton
                    onPress={() => navigation.navigate('AlarmCenterDetails', {})}
                    text={translate('register')}
                />}
            />

            <Card>
                <DataTableHeader
                    title={translate('registeredAlarmCenters')}
                    zIndex={2}
                    filterInput={<FilterInput
                        value={textFilter ?? ''}
                        placeholder={translate('cameraTitleIdOrSerialNumber')}
                        onChange={setTextFilter}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: translate('title'),
                                selector: row => row.title,
                                grow: 3,
                                wrap: true,
                            },
                            {
                                name: translate('installationCompany'),
                                selector: row => row.installationCompany?.name || '',
                            },
                            {
                                name: translate('internetType'),
                                selector: row => row.internetType == '5g' ? '5G' : row.internetType == '4g' ? '4G' : row.internetType == 'fiber' ? translate('fiber') : translate('radio'),
                            },
                            {
                                name: translate('serialNo'),
                                selector: row => row.serialNo,
                            },
                            {
                                name: translate('registeredAt'),
                                selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm'),
                                wrap: true
                            },
                            {
                                button: true,
                                cell: row => <DropdownButton
                                    actions={[
                                        {
                                            content: translate('editInformation'),
                                            onPress: () => navigation.navigate('AlarmCenterDetails', { id: row.id })
                                        }
                                    ]}
                                />
                            }
                        ]}
                        data={alarmCenters}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        totalItems={totalItems}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>
        </Container>

    );
}
